import { z } from "zod";
/**
 * Per-item-category diff entry. Matching is driven by compCode.
 * `label` is display-only and does not affect matching.
 */
export const ZRoomValidationDiffItem = z.object({
    /** Primary comparison key from domain (e.g., psets.NONS_Reference.RefCompType). */
    compCode: z.string().min(1),
    /** Optional descriptive label/type name for UI display. */
    label: z.string().min(1).nullable().optional(), // ← TILLÅT null + undefined
    /** Expected count taken from the standard room ("facit"). */
    standardCount: z.number().int().nonnegative(),
    /** Observed count in the actual room instance. */
    instanceCount: z.number().int().nonnegative(),
    /** Convenience: instanceCount - standardCount. */
    delta: z.number().int(),
});
/**
 * Summary KPIs for quick rendering and server-side querying.
 * All counts refer to distinct item categories (grouped by compCode).
 */
export const ZValidationStatus = z.enum([
    "OK",
    "WARNING",
    "ERROR",
    "invalidGeometry",
    "noStandardRoom",
]);
export const ZRoomValidationSummary = z.object({
    /** Match rate in percent (0..100). */
    matchRatePct: z.number().min(0).max(100),
    /** Number of distinct item categories in the standard room (compCode groups). */
    itemsTotal: z.number().int().nonnegative(),
    /** Number of categories that matched exactly (instanceCount === standardCount). */
    itemsMatched: z.number().int().nonnegative(),
    /** Number of categories that are missing/under-counted in the instance. */
    itemsMissing: z.number().int().nonnegative(),
    /** Number of categories that are extra/over-counted or unknown in the instance. */
    itemsExtra: z.number().int().nonnegative(),
    /** Overall status bucket for the room. */
    status: ZValidationStatus,
});
/**
 * High-level validation status used for UI filtering and coloring.
 */
export const ZRoomValidationSummaryLite = z.object({
    matchRatePct: z.number().min(0).max(100).nullable(),
});
export const ZRoomValidationStandardRoomDisciplineSummaryLite = z.object({
    summary: ZRoomValidationSummaryLite,
});
/**
 * SpecifiedArticles (list endpoint): overall + perResponsibility, summary-only
 */
export const ZRoomValidationSpecifiedArticlesSummaryLite = z.object({
    summary: ZRoomValidationSummaryLite,
    perResponsibility: z
        .record(z.string().min(1), z.object({
        summary: ZRoomValidationSummaryLite,
    }))
        .optional()
        .nullable(),
});
/* -------------------------------------------------------------------------------------------------
 * Summary-only validation (for list endpoints)
 * ------------------------------------------------------------------------------------------------- */
export const ZRoomValidationSummaryOnly = z.object({
    standardRoom: z
        .record(z.string(), ZRoomValidationStandardRoomDisciplineSummaryLite)
        .optional(),
    // null = finnes ikke / ikke kjørt / ikke relevant
    specifiedArticles: ZRoomValidationSpecifiedArticlesSummaryLite.optional().nullable(),
});
